import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "../components/Header";

const DIM_LABELS = { physical: "Physical", mental: "Mental & Emotional", spiritual: "Spiritual", relational: "Relational", vocational: "Vocational & Financial", aspirational: "Aspirational", legacy: "Legacy" };

function Rail({ eyebrow, note, items }) {
  return (
    <div className="py-10">
      <div className="flex items-end justify-between mb-6 px-6 lg:px-10 max-w-7xl mx-auto w-full">
        <div><p className="eyebrow mb-1">{eyebrow}</p><p className="font-headline italic text-lg text-ink/60">{note}</p></div>
      </div>
      <div className="flex gap-6 overflow-x-auto no-scrollbar px-6 lg:px-10 max-w-7xl mx-auto">
        {items.map((e) => (
          <Link key={e.slug} to={`/experiences/${e.slug}`} data-testid={`rail-card-${e.slug}`}
            className="group shrink-0 w-[300px]">
            <div className="relative aspect-[3/4] overflow-hidden">
              <img src={e.image} alt={e.name} className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              {e.exclusivity && <span className="absolute top-3 right-3 eyebrow !text-[0.55rem] bg-black/60 px-2 py-1 text-gold">Vetted · {e.exclusivity}</span>}
              <div className="absolute bottom-0 p-5 text-ivory">
                <p className="eyebrow !text-[0.55rem] mb-2">{e.status ? `${e.status} · ` : ""}{e.location}</p>
                <h4 className="font-headline text-xl leading-tight">{e.name}</h4>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function Results() {
  const nav = useNavigate();
  const [r, setR] = useState(null);
  useEffect(() => {
    const d = sessionStorage.getItem("wellthspan");
    if (!d) { nav("/diagnostic"); return; }
    setR(JSON.parse(d));
  }, [nav]);
  if (!r) return <div className="min-h-screen bg-ivory" />;

  const outputs = [
    { n: "01", t: "Imbalance Reading", v: r.lowest.label, s: "Your primary leak" },
    { n: "02", t: "Pathway", v: r.pathway.name, s: r.pathway.tagline },
    { n: "03", t: "Lifestay Format", v: r.pathway.format, s: "Recommended container" },
    { n: "04", t: "Matched Experience", v: r.matched.name, s: r.matched.location },
  ];

  return (
    <div className="min-h-screen bg-ivory">
      <Header variant="light" active="experiences" />
      <section className="pt-28 pb-10 px-6 lg:px-10 max-w-7xl mx-auto">
        <p className="eyebrow mb-4">Your WELLTHSPAN™ Reading</p>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] max-w-3xl">
          YOU DO NOT NEED ANOTHER HOLIDAY.
        </h1>
        <p className="mt-6 font-headline italic text-lg lg:text-xl text-ink/75 max-w-2xl leading-snug whitespace-pre-line" data-testid="reading-text">{r.reading}</p>
      </section>

      {/* Balanced Life Index bars */}
      <section className="px-6 lg:px-10 max-w-7xl mx-auto pb-4 grid sm:grid-cols-2 gap-x-12 gap-y-3">
        {Object.entries(r.scores).map(([k, v]) => (
          <div key={k}>
            <div className="flex justify-between text-xs mb-1"><span>{DIM_LABELS[k]}</span><span className="text-gold">{v}</span></div>
            <div className="h-px bg-ink/10"><motion.div initial={{ width: 0 }} animate={{ width: `${v}%` }} transition={{ duration: 0.9 }} className={`h-full ${k === r.lowest.key ? "bg-aqua" : "bg-gold"}`} /></div>
          </div>
        ))}
      </section>

      {/* four outputs */}
      <section className="px-6 lg:px-10 max-w-7xl mx-auto py-12 grid grid-cols-1 md:grid-cols-4 gap-px bg-gold/25 border border-gold/25">
        {outputs.map((o) => (
          <div key={o.n} data-testid={`output-${o.n}`} className="bg-ivory p-6 lg:p-8">
            <p className="font-display text-5xl text-gold/30">{o.n}</p>
            <p className="eyebrow mt-4 !text-ink/40">{o.t}</p>
            <p className="font-headline text-xl lg:text-2xl mt-2 leading-tight">{o.v}</p>
            <p className="text-xs text-ink/50 mt-2">{o.s}</p>
          </div>
        ))}
      </section>

      <Rail eyebrow="Rail 01 · LUXEWELL Signature" note="Owned, produced and guided by the house." items={r.rail_signature} />
      <div className="hairline max-w-7xl mx-auto" />
      <Rail eyebrow="Rail 02 · Curated for You on The LIST" note="Vetted partners matched to your reading." items={r.rail_curated} />
      <div className="px-6 lg:px-10 max-w-7xl mx-auto py-16 text-center">
        <Link to={`/experiences/${r.matched.slug}`} data-testid="results-view-matched" className="btn-lux bg-ink text-ivory hover:bg-gold hover:text-ink">View Your Matched Experience</Link>
      </div>
    </div>
  );
}
